// CLIENT-ONLY MODULE — do not import this from server code (Server
// Components, Route Handlers, server actions, etc.). The token is held in a
// single module-level variable; on the server that module is shared across
// every concurrent request/user in the process, so a server-side import
// would leak one user's access token into another user's requests. This
// cache exists solely to back the browser-side clientFetcher interceptor.
let currentAccessToken: string | undefined;

export const setAccessToken = (token: string | undefined): void => {
  currentAccessToken = token;
};

// There is no 401-retry interceptor, so this request-side expiry check is
// the sole recovery path — it must be conservative. 30s is a large buffer
// relative to the realm's 1h access-token lifespan (~0.8% of token life),
// so it costs us nothing but reliably avoids racing the server's own clock.
const CLOCK_SKEW_MS = 30_000;

function isTokenExpired(token: string): boolean {
  try {
    const payloadBase64url = token.split(".")[1];
    if (!payloadBase64url) return true;
    // JWT uses base64url (- and _ instead of + and /; no padding).
    // atob() requires standard base64 with padding, so we normalise first.
    const padded =
      payloadBase64url.replace(/-/g, "+").replace(/_/g, "/") +
      "=".repeat((4 - (payloadBase64url.length % 4)) % 4);
    const payload = JSON.parse(atob(padded)) as { exp?: number };
    if (typeof payload.exp !== "number") return false;
    // Treat as expired 30 s early so we refresh before the server rejects it.
    return Date.now() >= payload.exp * 1000 - CLOCK_SKEW_MS;
  } catch {
    return true;
  }
}

export const getAccessToken = (): string | undefined => {
  if (!currentAccessToken || isTokenExpired(currentAccessToken)) {
    return undefined;
  }
  return currentAccessToken;
};
